import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import Button from '../../../../components/Form/Button'
import Input from '../../../../components/Form/Input'
import Select from '../../../../components/Form/Select'
import { usStates } from '../../../../util/form'
import OnboardingSteps from './OnboardingSteps'
import OnboardingStep from './OnboardingStep'
import InvitationLinkRow from './InvitationLinkRow'
import {
  generateAccountInvitationApi,
  updateAgreementFromUploadApi,
  createAgreementFromUploadApi,
  getUtilityTariffsApi,
  type UtilityTariffOption,
} from '../../../../apis/apis'
import {
  archiveTenantRegistrationRecord,
  fetchCreateTenantPreview,
  fetchSalesforceRecordNames,
  previewRequiresConflictReview,
  mergeFeedIntoTenant,
  markRegistrationCompleteApi,
  normalizeUtilityAccountTenantType,
  updateTenantRegistrationRecord,
  unarchiveTenantRegistrationRecord,
  updateTESASupplementAgreementStatusApi,
  type CreateTenantPreview,
} from '../../../../apis/registration'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useFormik, Field, FormikProvider, getIn } from 'formik'
import { object, array, string } from 'yup'
import { formatPhoneNumber, formatTitleCase, formatUTCDate } from '../../../../util/string'
import type {
  TenantRegistration as Onboarding,
  UtilityAccount,
  ServiceAgreement,
  AddressComponents,
  AgreementSubmission,
  ExternallySignedAgreementDocument,
} from '../../../../types/registration'
import classnames from 'classnames'
import { format } from 'date-fns'
import {
  canShowOnboardingDocumentViewAction,
  TENANT_NOT_CREATED_DOCUMENT_MESSAGE,
} from '../document-view.constants'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import Datepicker from 'react-tailwindcss-datepicker'
import ComboboxAutocomplete from '../../../../components/Form/ComboboxAutocomplete'
import { getInputStyles } from '../../../../components/Form/styles'
import { EyeIcon } from '@heroicons/react/24/outline'
import { EyeSlashIcon } from '@heroicons/react/24/outline'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import ConfirmDialogShell from '../../tenant/components/billing/ConfirmDialogShell'
import ArchiveOnboardingDialog from './ArchiveOnboardingDialog'
import CreateTenantConflictModal from './CreateTenantConflictModal'
import CreateTenantActionWarnings from './CreateTenantActionWarnings'
import Panel from '../../../../components/Panel'
import { SALESFORCE_BASE_URL, LATEST_TESA_VERSION, parseTesaVersion, isNgomOrVnemTenantName } from '../constants'
import { RecordAttributeBadges } from './RecordAttributeBadges'
import {
  getUtilityAccountNumberWarning,
  getServiceAgreementNumberWarning,
  getMeterNumberWarning,
  getSalesforceIdWarning,
  isValidSalesforceId,
} from '../../../../util/accountNumberValidation'
import { getTariffScheduleWarning, sanitizeTariffSchedule } from '../../../../util/tariffValidation'

const nonEmptyTrimmedString = (requireStr: string) =>
  string()
    .transform((v) => (v == null ? '' : String(v)))
    .trim()
    .required(requireStr)

function hasTouchedNestedErrors(errs: unknown, tch: unknown): boolean {
  if (errs == null) return false
  if (typeof errs === 'string') return errs.length > 0 && tch === true
  if (Array.isArray(errs)) {
    const tArr = Array.isArray(tch) ? tch : []
    return errs.some((e, i) => hasTouchedNestedErrors(e, tArr[i]))
  }
  if (typeof errs === 'object') {
    const tObj = (tch ?? {}) as Record<string, unknown>
    return Object.entries(errs as Record<string, unknown>).some(([k, v]) =>
      hasTouchedNestedErrors(v, tObj[k])
    )
  }
  return false
}

function hasNestedFormErrors(e: unknown): boolean {
  if (e == null) return false
  if (typeof e === 'string') return e.length > 0
  if (Array.isArray(e)) return e.some(hasNestedFormErrors)
  if (typeof e === 'object') return Object.values(e as Record<string, unknown>).some(hasNestedFormErrors)
  return false
}

const VALID_STATE_CODES = new Set(Object.keys(usStates).filter((k) => k !== ''))

function buildRegistrationValidationSchema(options: {
  requireResponsibleParty: boolean
  requireStr: string
}) {
  const { requireResponsibleParty, requireStr } = options
  const phoneSchema = string()
    .required(requireStr)
    .test('phone', '10 digits required', (value) => {
      if (value == null || value === '') return false
      const digits = value.replace(/\D/g, '')
      return digits.length === 10
    })

  const stateCodeSchema = string()
    .transform((v) => (v == null ? '' : String(v)))
    .trim()
    .test('state-code', 'Expected a valid 2-letter US state code (e.g. CA)', (v) => !v || VALID_STATE_CODES.has(v.toUpperCase()))

  const zipCodeSchema = string()
    .transform((v) => (v == null ? '' : String(v)))
    .trim()
    .test('zip-code', 'Expected 5 digits (e.g. 92103) or 5-4 digits (e.g. 92103-1234)', (v) => !v || /^\d{5}(-\d{4})?$/.test(v))

  return object().shape({
    registration: object().shape({
      tenant: object().shape({
        name: nonEmptyTrimmedString(requireStr),
        ...(requireResponsibleParty
          ? {
            businessEntityName: nonEmptyTrimmedString(requireStr),
            businessEntityType: nonEmptyTrimmedString(requireStr),
            stateOfFormation: nonEmptyTrimmedString(requireStr),
          }
          : {}),
        contact: object().shape({
          firstName: nonEmptyTrimmedString(requireStr),
          lastName: nonEmptyTrimmedString(requireStr),
          emailAddress: string().email('Invalid email').required(requireStr),
          phoneNumber: phoneSchema,
          title: string().nullable(),
        }),
        accounts: array().of(
          object().shape({
            name: nonEmptyTrimmedString(requireStr),
            number: nonEmptyTrimmedString(requireStr),
            tenantType: string()
              .oneOf(['National', 'Local'], 'Choose National or Local account type')
              .required(requireStr),
            billingAddress: object().shape({
              state: stateCodeSchema,
              zipCode: zipCodeSchema,
            }),
            agreements: array().of(
              object().shape({
                number: nonEmptyTrimmedString(requireStr),
                meterNumber: nonEmptyTrimmedString(requireStr),
                tariffSchedule: string()
                  .transform((v) => (v == null ? '' : String(v)))
                  .test('tariff-schedule', requireStr, function (v) {
                    const tenantName = (this.from ?? []).find((ancestor) => {
                      const value = ancestor.value as { name?: string; accounts?: unknown[] } | undefined
                      return typeof value?.name === 'string' && Array.isArray(value.accounts)
                    })?.value as { name?: string } | undefined
                    if (isNgomOrVnemTenantName(tenantName?.name)) {
                      return true
                    }
                    return sanitizeTariffSchedule(v).length > 0
                  }),
                // Optional on the backend DTO (@IsOptional() @IsString()); empty strings are the serialization
                // convention (see updateTenantRegistrationRecord in apis/registration.ts). Do not require here.
                salesforceProgramId: string().nullable(),
                salesforceMeterId: string().nullable(),
                serviceAddress: object().shape({
                  address: nonEmptyTrimmedString(requireStr),
                  city: nonEmptyTrimmedString(requireStr),
                  state: stateCodeSchema.required(requireStr),
                  zipCode: zipCodeSchema.required(requireStr),
                }),
              })
            ),
          })
        ),
      }),
    }),
  })
}

const blankAddress = (): AddressComponents => ({
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
})

const blankServiceAgreement = (): ServiceAgreement => ({
  number: '',
  meterNumber: '',
  tariffSchedule: '',
  serviceAddress: blankAddress(),
  approximateEnergyUsage: null,
})

const blankUtilityAccount = (): UtilityAccount => ({
  name: '',
  number: '',
  tenantType: 'Local',
  billingAddress: blankAddress(),
  agreements: [blankServiceAgreement()],
})

function normalizeRegistrationForSave(registration: Onboarding): Onboarding {
  return {
    ...registration,
    tenant: {
      ...registration.tenant,
      accounts: registration.tenant.accounts.map((account) => ({
        ...account,
        tenantType: normalizeUtilityAccountTenantType(account.tenantType),
      })),
    },
  }
}

export interface OnboardingEditProps {
  onboarding: Onboarding
  refreshList: () => Promise<void>
  updateOnboardingFn: (onboarding: Onboarding) => Promise<void>
  isFullPage?: boolean
}

export const OnboardingRecordEdit: React.FC<OnboardingEditProps> = (props: OnboardingEditProps) => {
  const [isFetchingApi, setFetchingApi] = useState(false)
  const [showGenerateInviteConfirm, setShowGenerateInviteConfirm] = useState<boolean>(false)
  const [createTenantPreview, setCreateTenantPreview] = useState<CreateTenantPreview | null>(null)
  const [showCreateTenantConflict, setShowCreateTenantConflict] = useState(false)
  const [createDespiteConflicts, setCreateDespiteConflicts] = useState(false)
  const [showAllFieldErrors, setShowAllFieldErrors] = useState(false)
  const [showRemoveUaConfirm, setShowRemoveUaConfirm] = useState<boolean>(false)
  const [showRemoveSaConfirm, setShowRemoveSaConfirm] = useState<boolean>(false)
  const [showAddUaConfirm, setShowAddUaConfirm] = useState<boolean>(false)
  const [showAddSaConfirm, setShowAddSaConfirm] = useState<boolean>(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState<boolean>(false)
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState<boolean>(false)
  const [isUnarchiving, setIsUnarchiving] = useState<boolean>(false)
  const { permissions } = useAppSelector((state) => state.app)
  const hasAgreementUpdatePermission = permissions?.includes('account:manager:agreement:update') ?? false
  const hasArchivePermission = permissions?.includes('account:manager:onboarding:archive') ?? false

  // State for handling uploading new documents and changing existing documents
  const [showUploadDateModal, setShowUploadDateModal] = useState<boolean>(false)
  const [uploadedDate, setUploadedDate] = useState<{
    startDate: Date | null
    endDate: Date | null
  }>({
    startDate: new Date(),
    endDate: new Date(),
  })

  const [newUpload, setNewUpload] = useState<boolean | null>(null)
  const [selectedAgreementDocRefIndex, setSelectedAgreementDocRefIndex] = useState<number | null>(null)

  const [agreementDocType, setAgreementDocType] = useState<{ label: string; value: string }>({ label: 'Utility Authorization', value: 'TESA_SUPPLEMENT' })
  const [uploadTesaVersion, setUploadTesaVersion] = useState<string>(`v${LATEST_TESA_VERSION}`)
  const [uploadExpiresAt, setUploadExpiresAt] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null })
  const [uploadExpiresIndefinite, setUploadExpiresIndefinite] = useState<boolean>(false)

  const [showAgreementDocTypeOptions, setShowAgreementDocTypeOptions] = useState<boolean>(false)

  const [isSsnHidden, setIsSsnHidden] = useState<boolean>(true)

  const tesaSupplements = props.onboarding.invites?.ONBOARDING_REGISTRATION?.agreementSubmissions ?? []

  const externallySignedAgreementDocs = props.onboarding.invites?.ONBOARDING_REGISTRATION?.externallySignedAgreementDocuments ?? []
  const externallySignedTesas = externallySignedAgreementDocs.filter((doc) => doc.type === 'TESA')

  const agreementDocTypes =
    externallySignedTesas.length < 1
      ? [
          { name: 'TESA', id: 'TESA' },
          { name: 'Utility Authorization', id: 'TESA_SUPPLEMENT' },
        ]
      : [{ name: 'Utility Authorization', id: 'TESA_SUPPLEMENT' }]

  const dispatch = useAppDispatch()
  const requireStr = 'This field is required'

  const registrationInitial = useMemo(() => {
    const r = props.onboarding
    const rawAccounts = r.tenant?.accounts ?? []
    const mappedAccounts = rawAccounts.map((account) => ({
      ...account,
      tenantType: normalizeUtilityAccountTenantType(account.tenantType),
    }))
    const accounts = mappedAccounts.length > 0 ? mappedAccounts : [blankUtilityAccount()]
    return {
      ...r,
      tenant: {
        ...r.tenant,
        accounts,
      },
    }
  }, [props.onboarding])

  const validationSchema = useMemo(() => {
    const onb = props.onboarding.invites?.ONBOARDING_REGISTRATION
    const isExternalTesa = (onb?.externallySignedAgreementDocuments ?? []).some((d) => d.type === 'TESA')
    const isLegacyFlow = parseTesaVersion(onb?.tesaVersion ?? null).isLegacy
    const requireResponsibleParty = !isLegacyFlow && !isExternalTesa
    return buildRegistrationValidationSchema({ requireResponsibleParty, requireStr })
  }, [props.onboarding, requireStr])

  const formik = useFormik({
    initialValues: {
      registration: registrationInitial,
      selectedAccountIdx: 0,
      selectedAgreementIdx: 0,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await props.updateOnboardingFn(normalizeRegistrationForSave(values.registration))
    },
  })
  const { values, errors, touched, dirty, isSubmitting, submitCount, setFieldValue, handleBlur, handleSubmit, validateField, validateForm } = formik

  const sfNameLookupSeqRef = useRef<Record<string, number>>({})
  const [sfProgramNameMissing, setSfProgramNameMissing] = useState(false)
  const [sfMeterNameMissing, setSfMeterNameMissing] = useState(false)

  // Seed missing-name warnings from GET enrichment when switching agreement / reloading.
  useEffect(() => {
    const sa =
      props.onboarding.tenant.accounts[values.selectedAccountIdx]?.agreements?.[values.selectedAgreementIdx]
    const programId = sa?.salesforceProgramId?.trim() ?? ''
    const meterId = sa?.salesforceMeterId?.trim() ?? ''
    setSfProgramNameMissing(
      programId !== '' && isValidSalesforceId(programId) && !sa?.salesforceProgramName
    )
    setSfMeterNameMissing(meterId !== '' && isValidSalesforceId(meterId) && !sa?.salesforceMeterName)
  }, [props.onboarding, values.selectedAccountIdx, values.selectedAgreementIdx])

  const refreshAgreementSalesforceName = useCallback(
    async (
      kind: 'program' | 'meter',
      rawId: string | null | undefined,
      accountIdx: number,
      agreementIdx: number
    ): Promise<void> => {
      const namePath =
        kind === 'program'
          ? `registration.tenant.accounts[${accountIdx}].agreements[${agreementIdx}].salesforceProgramName`
          : `registration.tenant.accounts[${accountIdx}].agreements[${agreementIdx}].salesforceMeterName`
      const setMissing = kind === 'program' ? setSfProgramNameMissing : setSfMeterNameMissing
      const trimmed = rawId?.trim() ?? ''
      const seqKey = `${kind}:${accountIdx}:${agreementIdx}`
      sfNameLookupSeqRef.current[seqKey] = (sfNameLookupSeqRef.current[seqKey] ?? 0) + 1
      const seq = sfNameLookupSeqRef.current[seqKey]

      if (trimmed === '' || !isValidSalesforceId(trimmed)) {
        setFieldValue(namePath, null)
        setMissing(false)
        return
      }

      try {
        const names = await fetchSalesforceRecordNames(
          kind === 'program' ? { programId: trimmed } : { meterId: trimmed }
        )
        if (sfNameLookupSeqRef.current[seqKey] !== seq) return
        const resolved = kind === 'program' ? names.programName : names.meterName
        setFieldValue(namePath, resolved)
        setMissing(resolved == null || resolved === '')
      } catch {
        if (sfNameLookupSeqRef.current[seqKey] !== seq) return
        setFieldValue(namePath, null)
        // Network/API failure — don't claim the SF record is missing.
        setMissing(false)
      }
    },
    [setFieldValue]
  )

  const revealFieldErrors = submitCount > 0 || showAllFieldErrors

  const showFormErrorSummary = revealFieldErrors && hasNestedFormErrors(errors)

  const accountErrorsForIdx = (i: number): unknown => {
      const accts = (errors as any)?.registration?.tenant?.accounts
      return Array.isArray(accts) ? accts[i] : undefined
  }
  const accountTouchedForIdx = (i: number): unknown => {
    const accts = (touched as any)?.registration?.tenant?.accounts
    return Array.isArray(accts) ? accts[i] : undefined
  }
  const fieldError = (path: string): string | undefined => {
      const err = getIn(errors, path)
      if (typeof err !== 'string') return undefined
      const isTouched = getIn(touched, path) === true
      return isTouched || revealFieldErrors ? err : undefined
  }
  const uaAnyErrors = (i: number): boolean => hasNestedFormErrors(accountErrorsForIdx(i))
  const saAnyErrors = (uaIdx: number, saIdx: number): boolean => {
      const uaErr = accountErrorsForIdx(uaIdx) as any
      const agreements = uaErr?.agreements
      return Array.isArray(agreements) && hasNestedFormErrors(agreements[saIdx])
  }
  const uaHasErrors = (i: number): boolean => {
    if (revealFieldErrors) return uaAnyErrors(i)
    return hasTouchedNestedErrors(accountErrorsForIdx(i), accountTouchedForIdx(i))
  }
  const saHasErrors = (uaIdx: number, saIdx: number): boolean => {
    if (revealFieldErrors) return saAnyErrors(uaIdx, saIdx)
    const uaErr = accountErrorsForIdx(uaIdx) as any
    const uaTch = accountTouchedForIdx(uaIdx) as any
    const errAgreements = uaErr?.agreements
    const tchAgreements = uaTch?.agreements
    if (!Array.isArray(errAgreements)) return false
    return hasTouchedNestedErrors(errAgreements[saIdx], Array.isArray(tchAgreements) ? tchAgreements[saIdx] : undefined)
  }
  const saHasWarnings = (uaIdx: number, saIdx: number): boolean => {
    if (saHasErrors(uaIdx, saIdx)) return false
    const utility = values.registration.utility
    const sa = values.registration.tenant.accounts[uaIdx]?.agreements?.[saIdx]
    if (!sa) return false
    const isSelected =
      uaIdx === values.selectedAccountIdx && saIdx === values.selectedAgreementIdx
    return (
      getServiceAgreementNumberWarning(sa.number, utility) != null ||
      getMeterNumberWarning(sa.meterNumber, utility) != null ||
      getSalesforceIdWarning(sa.salesforceProgramId) != null ||
      getSalesforceIdWarning(sa.salesforceMeterId) != null ||
      (isSelected && sfProgramNameMissing) ||
      (isSelected && sfMeterNameMissing)
    )
  }
  const uaHasWarnings = (uaIdx: number): boolean => {
    if (uaHasErrors(uaIdx)) return false
    const utility = values.registration.utility
    const ua = values.registration.tenant.accounts[uaIdx]
    if (!ua) return false
    if (getUtilityAccountNumberWarning(ua.number, utility) != null) return true
    const agreements = ua.agreements ?? []
    for (let saIdx = 0; saIdx < agreements.length; saIdx++) {
      if (saHasWarnings(uaIdx, saIdx)) return true
    }
    return false
  }
  const recordHasFormatWarnings = (() => {
    const utility = values.registration.utility
    const accounts = values.registration.tenant.accounts ?? []
    for (const ua of accounts) {
      if (getUtilityAccountNumberWarning(ua.number, utility) != null) return true
      for (const sa of ua.agreements ?? []) {
        if (
          getServiceAgreementNumberWarning(sa.number, utility) != null ||
          getMeterNumberWarning(sa.meterNumber, utility) != null ||
          getSalesforceIdWarning(sa.salesforceProgramId) != null ||
          getSalesforceIdWarning(sa.salesforceMeterId) != null
        ) {
          return true
        }
      }
    }
    return false
  })()

  const accountOptions = useMemo(() => {
    const options = values.registration.tenant.accounts.map((account, idx) => ({
      value: `${idx}`,
      label: account.name,
    }))

    return options
  }, [values.registration.tenant.accounts])

  const agreementOptions = useMemo(() => {
    const options = (values.registration.tenant.accounts[values.selectedAccountIdx].agreements ?? []).map((agreement, idx) => ({
      value: `${idx}`,
      label: agreement.number,
    }))

    return options
  }, [values.registration.tenant.accounts, values.selectedAccountIdx])

  const {
    ONBOARDING_REGISTRATION,
    ACCOUNT_SIGNUP,
    isOnregInviteCreated,
    isOnregInvitePending,
    isOnregAgreedPricingAndPayments,
    isOnregInviteConsumed,
    isAccountInviteCreated,
    isAccountInviteConsumed,
  } = values.registration.invites
  const hasLinkedTenant = Boolean(values.registration.tenantId)
  const documentViewActionBase = { hasLinkedTenant, isAccountInviteCreated }

  const envPrefix = window.location.host !== 'admin.kingenergy.com' ? 'dev.' : ''
  const onregUrl = `https://${envPrefix}registration.kingenergy.com/${ONBOARDING_REGISTRATION?.code ?? ''}`
  const onebillUrl = `https://${envPrefix}service.kingenergy.com/register?invitationCode=${ACCOUNT_SIGNUP?.code ?? ''}`
  const showOnregInvitationRow = !isAccountInviteCreated && !!ONBOARDING_REGISTRATION?.code
  const showOnebillInvitationRow = isAccountInviteCreated && !isAccountInviteConsumed && !!ACCOUNT_SIGNUP?.code
  /**
   * Allow add/remove of UAs and SAs only pre-account-creation AND while the record is active.
   * Tenant still re-authorizes via PMA on UA changes. Archived records are read-only — without the
   * `archivedAt` check the buttons would still render (archival itself requires !isAccountInviteCreated),
   * letting users mutate Formik state they can never persist (Save is hidden + BE rejects edits).
   */
  const canAddRemoveUaSa = !isAccountInviteCreated && props.onboarding.archivedAt == null

  const tesaVersion = ONBOARDING_REGISTRATION?.tesaVersion ?? null
  const parsedTesa = parseTesaVersion(tesaVersion)
  const isLegacyFlow = parsedTesa.isLegacy

  const externalTesaDocs = externallySignedAgreementDocs.filter((d) => d.type === 'TESA')
  const isExternalTesa = externalTesaDocs.length > 0
  const externalSupplementDocs = externallySignedAgreementDocs.filter((d) => d.type === 'TESA_SUPPLEMENT')
  const externalTesaCuids = new Set(externalTesaDocs.map((d) => d.agreementId))
  const externalSupplementCuids = new Set(externalSupplementDocs.map((d) => d.agreementId))
  const tesaAgreement = isExternalTesa ? undefined : ONBOARDING_REGISTRATION?.agreements?.find((a) => a.type === 'TESA' && !externalTesaCuids.has(a.cuid))
  const tesaAcceptedAt = tesaAgreement?.acceptedAt ?? undefined
  const externalTesaSignedAt = externalTesaDocs[0]?.dateSigned ?? undefined
  /** Lock authorized business entity fields after TESA is accepted (inline acceptance or externally signed TESA). */
  const lockAuthorizedEntityFields = Boolean(tesaAcceptedAt || externalTesaSignedAt)
  const supplementAgreement = ONBOARDING_REGISTRATION?.agreements?.find((a) => a.type === 'TESA_SUPPLEMENT' && !externalSupplementCuids.has(a.cuid))
  const supplementAcceptedAt = supplementAgreement?.acceptedAt ?? undefined
  const externalSupplementSignedAt = externalSupplementDocs[0]?.dateSigned ?? undefined
  const hasExternalSupplement = externalSupplementDocs.length > 0
  const paymentAcceptedAt = ONBOARDING_REGISTRATION?.agreements?.find((a) => a.type === 'PRICING_AND_PAYMENT_TERMS')?.acceptedAt ?? undefined

  /** Warn in the "Create tenant account" modal if the latest PMA is still pending/rejected and no external PMA exists. */
  const latestPmaSubmission: AgreementSubmission | null = tesaSupplements
    .filter((s: AgreementSubmission) => s.agreementType === 'TESA_SUPPLEMENT')
    .reduce<AgreementSubmission | null>((latest, curr) => {
      if (!latest) return curr
      return new Date(curr.statusDate).getTime() > new Date(latest.statusDate).getTime() ? curr : latest
    }, null)
  const pmaWarningKind: 'rejected' | 'pending' | null =
    !hasExternalSupplement && latestPmaSubmission?.status === 'REJECTED'
      ? 'rejected'
      : !hasExternalSupplement && latestPmaSubmission?.status === 'SUBMITTED'
        ? 'pending'
        : null
  /** Any PMA — inline submission (any status) or an externally-uploaded supplement — means UA/SA changes require re-authorization. */
  const hasAnyPma = latestPmaSubmission != null || hasExternalSupplement

  /**
   * Align with invitation.consumed only — do not use feed isComplete (isFeedComplete = required
   * fields filled, true while onboarding is still in progress). NGOM accounts can be marked
   * complete with no TESA / PMA on file, so we cannot require any agreement to be present.
   */
  const registrationCompleteStepActive = isOnregInviteConsumed

  const utilityAccountIdsUserInput = ONBOARDING_REGISTRATION?.utilityAccountIdsUserInput ?? []
  // Baseline for the "Authorized by Tenant" diff is the server-persisted UA list (what the tenant was
  // presented with at PMA time), NOT the live Formik values — otherwise Admin in-session add/remove of
  // UAs here would spuriously render as tenant-initiated "Added"/"Removed" in the diff block below.
  const invitationAccountNumbers = (props.onboarding.tenant.accounts ?? []).map((a) => a.number).filter((n) => !!n)

  const agreementInvitationCuid = props.onboarding.invites?.ONBOARDING_REGISTRATION?.id ?? ''

  /** Tenant Team: PMA status does not lock this form. Only "Leaseholder's Authorized Business Entity" is read-only after TESA (see lockAuthorizedEntityFields). Archived records are read-only — backend rejects edit() with 409 when archivedAt != null. */
  const isArchived = props.onboarding.archivedAt != null
  const canEditForm = !isArchived

  /** Same permission gates Archive, Unarchive, and the "Show archived" filter on the list. */
  const canArchive = hasArchivePermission && !isArchived && !isAccountInviteCreated
  const canUnarchive = hasArchivePermission && isArchived

  /**
   * "Past invitation generated" — drives the warning callout in the Archive dialog so admins
   * see when meaningful onboarding progress is being hidden. Mirrors the booleans the steps
   * panel already uses (see lines around `OnboardingStep` rendering below).
   */
  const isOnregAgreedTesa = props.onboarding.invites.isOnregAgreedTesa
  const isOnregAgreedTesaSupplement = props.onboarding.invites.isOnregAgreedTesaSupplement
  const progressBeyondInvite =
    !!tesaAcceptedAt ||
    !!externalTesaSignedAt ||
    !!supplementAcceptedAt ||
    !!externalSupplementSignedAt ||
    !!paymentAcceptedAt ||
    isOnregInviteConsumed ||
    (isOnregInvitePending && (isOnregAgreedTesa || isOnregAgreedTesaSupplement || isOnregAgreedPricingAndPayments))

  // Ref for new file input
  const uploadNewDocInputRef = useRef<HTMLInputElement>(null)

  // Array of refs for "Change Document..." input
  const changeDocInputRefs = useRef<HTMLInputElement[]>([])

  useEffect(() => {
    // Adjust the refs array to match the number of documents
    changeDocInputRefs.current = externallySignedAgreementDocs.map((_, i) => changeDocInputRefs.current[i] || React.createRef())
  }, [externallySignedAgreementDocs.length])

  // All tariff schedule codes known to the database. Loaded unscoped by utility — a tenant's
  // service agreement can be on a non-IOU / CCA tariff that wouldn't appear under the
  // record's utility.
  const [tariffOptions, setTariffOptions] = useState<UtilityTariffOption[]>([])
  useEffect(() => {
    let cancelled = false
    void getUtilityTariffsApi()
      .then((opts) => {
        if (!cancelled) setTariffOptions(opts)
      })
      .catch(() => {
        if (!cancelled) setTariffOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])
  const knownTariffs = useMemo(
    () => tariffOptions.map((t) => ({ schedule: t.schedule, utilityShortName: t.utilityShortName })),
    [tariffOptions]
  )

  const handleUploadClick = (newSubmission: boolean) => {
    setShowUploadDateModal(true)

    setNewUpload(newSubmission)
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    newSubmission: boolean,
    agreementType: string,
    agreementCuid?: string
  ): Promise<void> => {
    const file = event.target.files?.[0]

    if (file == null) {
      return
    }

    if (newSubmission === false && agreementCuid == null) {
      throw new Error('agreementCuid must be provided when newSubmission is false')
    }

    // Create a FormData object and append the file, signed date, and agreement type
    const formData = new FormData()
    formData.append('file', file)

    const { startDate } = uploadedDate

    const baseDate = startDate != null ? new Date(startDate) : new Date()
    baseDate.setUTCHours(0, 0, 0, 0)
    const submissionDateTimeInMilliseconds = baseDate.getTime()

    formData.append('dateSigned', submissionDateTimeInMilliseconds + '')
    formData.append('type', agreementType)

    if (agreementType === 'TESA') {
      formData.append('version', uploadTesaVersion)
    }

    if (agreementType === 'TESA_SUPPLEMENT') {
      if (uploadExpiresIndefinite) {
        formData.append('expiresAt', 'indefinite')
      } else if (uploadExpiresAt.startDate != null) {
        const expiresDate = new Date(uploadExpiresAt.startDate)
        expiresDate.setUTCHours(0, 0, 0, 0)
        const expiresAtMs = expiresDate.getTime()
        formData.append('expiresAt', expiresAtMs + '')
      }
    }

    try {
      if (newSubmission === true) {
        await createAgreementFromUploadApi(agreementInvitationCuid, formData)
      } else {
        await updateAgreementFromUploadApi(agreementCuid as string, formData)
      }
      await dispatch(pushToast({ type: 'success', message: 'File uploaded' }))
      setUploadedDate({ startDate: null, endDate: null })
      setUploadExpiresAt({ startDate: null, endDate: null })
      setUploadExpiresIndefinite(false)
      setUploadTesaVersion(`v${LATEST_TESA_VERSION}`)
      await props.refreshList()

      // Reset the input value so the same file can be uploaded again
      event.target.value = ''

      // Set selected doc type back to TESA_SUPPLEMENT
      setAgreementDocType({ label: 'Utility Authorization', value: 'TESA_SUPPLEMENT' })
    } catch (e: any) {
      console.error(e)
      await dispatch(
        pushToast({
          type: 'error',
          message: 'Error uploading document',
          description: e?.response?.data?.message ?? e?.message,
        })
      )
    }
  }

  const handleArchiveConfirm = async (): Promise<void> => {
    try {
      await archiveTenantRegistrationRecord(props.onboarding.id)
      // Refresh so the parent re-fetches and re-passes `props.onboarding` with archivedAt set.
      // This flips `isArchived` true → banner appears, form goes read-only, Archive hides,
      // Unarchive shows. Avoids navigate(-1), which strands users who deep-linked here.
      await props.refreshList()
      dispatch(pushToast({ type: 'success', message: 'Onboarding record archived' }))
      setShowArchiveConfirm(false)
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to archive record',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        })
      )
    }
  }

  const handleUnarchiveConfirm = async (): Promise<void> => {
    setIsUnarchiving(true)
    try {
      await unarchiveTenantRegistrationRecord(props.onboarding.id)
      await props.refreshList()
      dispatch(pushToast({ type: 'success', message: 'Onboarding record restored' }))
      setShowUnarchiveConfirm(false)
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to restore record',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        })
      )
    } finally {
      setIsUnarchiving(false)
    }
  }

  /**
   * NGOM/VNEM accounts (KE-internal solar production / VNEM allocation entities, detected by
   * the tenant's business display name) skip the TESA + PMA + OneBill-account portions of
   * onboarding entirely (no tenant-facing billing for these). "Tenant created" is still shown —
   * the tenant entity is needed for meter / property association even on NGOM. Used to hide
   * those steps from the timeline and to bypass the agreement-presence gate on
   * "Mark registration complete." The backend re-checks the same name signal so the relaxation
   * is server-side authoritative.
   */
  const isNgomAccount = isNgomOrVnemTenantName(values.registration.tenant?.name)
  const isNationalAccount = props.onboarding.isNational === true
  const isAgreementExempt = isNgomAccount || isNationalAccount

  /**
   * Mark Complete button visibility:
   * Show before "Registration complete" and "Tenant created" — not gated on TESA/PMA status,
   * NGOM/National vs Local. The backend still validates agreement presence
   * (NGOM/VNEM and National exempt) when the action is submitted.
   */
  const showMarkCompleteButton = !isArchived && !isOnregInviteConsumed && !isAccountInviteCreated

  const handleMarkRegistrationComplete = async (): Promise<void> => {
    setFetchingApi(true)
    try {
      await markRegistrationCompleteApi(props.onboarding.id)
      await props.refreshList()
      dispatch(pushToast({ type: 'success', message: 'Registration marked complete' }))
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to mark registration complete',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        })
      )
    } finally {
      setFetchingApi(false)
    }
  }

  const ensureRegistrationReadyForFeedAction = async (): Promise<boolean> => {
    const validationErrors = await validateForm()
    if (hasNestedFormErrors(validationErrors)) {
      setShowAllFieldErrors(true)
      dispatch(
        pushToast({
          type: 'error',
          message: 'Cannot create tenant yet',
          description: 'Fix the highlighted fields and save before creating the tenant.',
        })
      )
      return false
    }

    if (!dirty) {
      return true
    }

    const registration = normalizeRegistrationForSave(values.registration)
    try {
      const result = await updateTenantRegistrationRecord(props.onboarding.id, registration)
      if (result.result !== 'success') {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Failed to save registration',
            description: 'Save your changes before creating the tenant.',
          })
        )
        return false
      }
      await props.refreshList()
      return true
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to save registration',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        })
      )
      return false
    }
  }

  const runCreateTenant = async (): Promise<void> => {
    setFetchingApi(true)
    try {
      if (!(await ensureRegistrationReadyForFeedAction())) {
        return
      }

      await generateAccountInvitationApi([ONBOARDING_REGISTRATION!.id], {
        forceCreateDespiteConflicts: createDespiteConflicts,
      })
      await props.refreshList()
      dispatch(
        pushToast({
          type: 'success',
          message: 'Account invitation generated',
        }),
      )
      setShowGenerateInviteConfirm(false)
      setShowCreateTenantConflict(false)
      setCreateTenantPreview(null)
      setCreateDespiteConflicts(false)
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to generate account invitation',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        }),
      )
    } finally {
      setFetchingApi(false)
    }
  }

  const handleCreateTenantClick = async (): Promise<void> => {
    setFetchingApi(true)
    setCreateDespiteConflicts(false)
    try {
      if (!(await ensureRegistrationReadyForFeedAction())) {
        return
      }

      const preview = await fetchCreateTenantPreview(props.onboarding.id)
      setCreateTenantPreview(preview)
      if (previewRequiresConflictReview(preview)) {
        setShowCreateTenantConflict(true)
      } else {
        setShowGenerateInviteConfirm(true)
      }
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to check for tenant conflicts',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        }),
      )
    } finally {
      setFetchingApi(false)
    }
  }

  const handleMergeIntoExistingTenant = async (targetTenantAccountCuid: string): Promise<void> => {
    setFetchingApi(true)
    try {
      if (!(await ensureRegistrationReadyForFeedAction())) {
        return
      }

      const result = await mergeFeedIntoTenant(props.onboarding.id, targetTenantAccountCuid)
      await props.refreshList()
      dispatch(
        pushToast({
          type: 'success',
          message: 'Meters added to existing tenant',
          description:
            result.metersAdded.length > 0 ? `Added meter(s): ${result.metersAdded.join(', ')}.` : undefined,
        }),
      )
      setShowCreateTenantConflict(false)
      setCreateTenantPreview(null)
    } catch (error: any) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to merge into existing tenant',
          description: error.response?.data?.message ?? error.response?.message ?? error.message,
        }),
      )
    } finally {
      setFetchingApi(false)
    }
  }

  return (
    <div className={classnames('h-full rounded-2xl justify-center my-8 p-0 text-left', { 'max-w-screen-lg': !props.isFullPage })}>
      <FormikProvider value={formik}>
        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {isArchived && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-4 py-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <div className="font-medium text-amber-900 dark:text-amber-100">This record is archived.</div>
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  Editing is disabled. Restore the record to make changes.
                </div>
              </div>
              {canUnarchive && (
                <Button color="indigo" onClick={() => setShowUnarchiveConfirm(true)} disabled={isUnarchiving} className="self-center">
                  Unarchive
                </Button>
              )}
            </div>
          )}
          <Panel
            header={
              <div className="flex items-center justify-between">
                <span>Registration Progress</span>
                <RecordAttributeBadges
                  record={props.onboarding}
                  size="detail"
                  className="flex items-center gap-2 font-normal text-sm"
                />
              </div>
            }
          >
            <OnboardingSteps
              className={classnames('grid', {
                // Step counts:
                //   NGOM:                              3 (invite, registration complete, tenant)
                //   National (agreement-exempt):       4 (invite, registration complete, tenant, onebill)
                //   Default w/o payment step:          6 (invite, TESA, PMA, registration complete, tenant, onebill)
                //   Default w/ payment step (legacy):  7 (default + payment)
                'grid-cols-3': isNgomAccount,
                'grid-cols-4': isNationalAccount && !isNgomAccount,
                'grid-cols-7': !isAgreementExempt && (isLegacyFlow || !!paymentAcceptedAt),
                'grid-cols-6': !isAgreementExempt && !isLegacyFlow && !paymentAcceptedAt,
              })}
            >
              <OnboardingStep
                title="Registration invitation generated"
                timestamp={ONBOARDING_REGISTRATION?.createDate}
                showTimestamp
                isActive={isOnregInviteCreated || !!ONBOARDING_REGISTRATION?.createDate}
              ></OnboardingStep>

              {!isAgreementExempt ? (
                <OnboardingStep
                  title="TESA accepted"
                  timestamp={isExternalTesa ? externalTesaSignedAt?.toString() : tesaAcceptedAt}
                  showTimestamp={!!tesaAcceptedAt || !!externalTesaSignedAt}
                  dateOnly={isExternalTesa}
                  showChildren={!!tesaVersion}
                  isActive={!!tesaAcceptedAt || !!externalTesaSignedAt}
                >
                  {tesaVersion && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-1 justify-center">
                      {isExternalTesa && (
                        <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded">External</span>
                      )}
                      {tesaVersion !== 'custom' ? (
                        <>
                          <span
                            className={`px-1.5 py-0.5 rounded ${
                              parsedTesa.isLegacy
                                ? 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200'
                                : parsedTesa.isCurrent
                                  ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                  : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                            }`}
                          >
                            {tesaVersion}
                          </span>
                        </>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded">Custom</span>
                      )}
                    </span>
                  )}
                </OnboardingStep>
              ) : null}

              {!isAgreementExempt ? (
                <OnboardingStep
                  title="Utility Authorization (PMA/CISR)"
                  timestamp={hasExternalSupplement ? externalSupplementSignedAt?.toString() : supplementAcceptedAt}
                  showTimestamp={hasExternalSupplement ? !!externalSupplementSignedAt : !!supplementAcceptedAt}
                  dateOnly={hasExternalSupplement}
                  showChildren={hasExternalSupplement}
                  isActive={!!supplementAcceptedAt || !!externalSupplementSignedAt}
                >
                  {hasExternalSupplement && (
                    <span className="text-xs">
                      <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded">External</span>
                    </span>
                  )}
                </OnboardingStep>
              ) : null}

              {!isAgreementExempt && (isLegacyFlow || !!paymentAcceptedAt) ? (
                <OnboardingStep
                  title="Payments &amp; pricing terms accepted"
                  timestamp={paymentAcceptedAt}
                  showTimestamp={!!paymentAcceptedAt}
                  isActive={!!paymentAcceptedAt || (isOnregInvitePending && isOnregAgreedPricingAndPayments)}
                ></OnboardingStep>
              ) : null}

              <OnboardingStep
                title="Registration complete"
                timestamp={ONBOARDING_REGISTRATION?.lastUpdatedDate}
                showTimestamp={registrationCompleteStepActive}
                isActive={registrationCompleteStepActive}
              ></OnboardingStep>

              <OnboardingStep
                title="Tenant created"
                timestamp={ACCOUNT_SIGNUP?.createDate}
                showTimestamp={isAccountInviteCreated}
                isActive={isAccountInviteCreated}
              ></OnboardingStep>

              {!isNgomAccount ? (
                <OnboardingStep
                  title="OneBill account created"
                  timestamp={ACCOUNT_SIGNUP?.lastUpdatedDate}
                  showTimestamp={isAccountInviteConsumed}
                  isActive={isAccountInviteConsumed}
                ></OnboardingStep>
              ) : null}
            </OnboardingSteps>
          </Panel>
          <Panel header="Business Information">
            <div className="grid grid-cols-12 gap-y-4 gap-x-4">
              <div className="col-span-6 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Business display name"
                  name="registration.tenant.name"
                  required
                  displayField={!canEditForm}
                  value={values.registration.tenant.name}
                  disabled={isSubmitting}
                  error={
                    errors.registration?.tenant?.name !== undefined && (touched.registration?.tenant?.name ?? false)
                      ? errors.registration?.tenant?.name
                      : undefined
                  }
                />
              </div>
              <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                {!canEditForm ? (
                  <Field
                    as={Input}
                    label="Account Type"
                    name="registration.tenant.accounts[0].tenantType"
                    displayField={true}
                    value={values.registration.tenant.accounts?.[0]?.tenantType ?? 'Local'}
                    disabled={true}
                  />
                ) : (
                  <Field
                    component={Select}
                    label="Account Type"
                      name="registration.tenant.accounts[0].tenantType"
                    value={values.registration.tenant.accounts?.[0]?.tenantType ?? 'Local'}
                    disabled={isSubmitting}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const newType = normalizeUtilityAccountTenantType(e.target.value)
                      values.registration.tenant.accounts?.forEach((_, i) => {
                        void setFieldValue(`registration.tenant.accounts[${i}].tenantType`, newType)
                      })
                    }}
                  >
                    <option value="Local">Local Account</option>
                    <option value="National">National Account</option>
                  </Field>
                )}
                {getIn(touched, 'registration.tenant.accounts[0].tenantType') === true &&
                  typeof getIn(errors, 'registration.tenant.accounts[0].tenantType') === 'string' && (
                    <p className="mt-1 text-xs text-red-600">{getIn(errors, 'registration.tenant.accounts[0].tenantType') as string}</p>
                  )}
              </div>
            </div>
          </Panel>
          {!isLegacyFlow && !isExternalTesa ? (
            <Panel header="Responsible Party">
              {lockAuthorizedEntityFields && (
                <div className="flex items-start gap-2 mb-4 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <LockClosedIcon className="h-4 w-4 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Authorized business entity fields below are locked after TESA acceptance.
                  </p>
                </div>
              )}
              <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Leaseholder's Authorized Business Entity</div>
              <div className="grid grid-cols-12 gap-y-4 gap-x-4">
                <div className="col-span-4 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Business Entity Name"
                    name="registration.tenant.businessEntityName"
                    required
                    displayField={!canEditForm || lockAuthorizedEntityFields}
                    value={values.registration.tenant.businessEntityName ?? ''}
                    disabled={isSubmitting || lockAuthorizedEntityFields}
                    error={
                      errors.registration?.tenant?.businessEntityName !== undefined &&
                        (touched.registration?.tenant?.businessEntityName ?? false)
                        ? errors.registration?.tenant?.businessEntityName
                        : undefined
                    }
                  />
                </div>
                <div className="col-span-4 flex flex-col text-slate-900 dark:text-slate-100">
                  {lockAuthorizedEntityFields ? (
                    <Field
                      as={Input}
                      label="Business Entity Type"
                      name="registration.tenant.businessEntityType"
                      displayField={true}
                      value={values.registration.tenant.businessEntityType ?? ''}
                      disabled={true}
                    />
                  ) : (
                    <Field
                      component={Select}
                      label="Business Entity Type"
                      name="registration.tenant.businessEntityType"
                      required
                      disabled={isSubmitting || !canEditForm}
                    >
                      <option value=""></option>
                      <option value="Corporation">Corporation</option>
                      <option value="LLC">LLC</option>
                      <option value="Limited Partnership">Limited Partnership</option>
                      <option value="Limited Liability Partnership">Limited Liability Partnership</option>
                      <option value="General Partnership">General Partnership</option>
                      <option value="Sole Proprietor">Sole Proprietor</option>
                      <option value="Nonprofit Corporation">Nonprofit Corporation</option>
                      <option value="Trust">Trust</option>
                      <option value="Other">Other</option>
                    </Field>
                  )}
                  {!lockAuthorizedEntityFields &&
                    getIn(touched, 'registration.tenant.businessEntityType') === true &&
                    typeof getIn(errors, 'registration.tenant.businessEntityType') === 'string' && (
                      <p className="mt-1 text-xs text-red-600">{getIn(errors, 'registration.tenant.businessEntityType') as string}</p>
                    )}
                </div>
                <div className="col-span-4 flex flex-col text-slate-900 dark:text-slate-100">
                  {lockAuthorizedEntityFields ? (
                    <Field
                      as={Input}
                      label="State of Formation"
                      name="registration.tenant.stateOfFormation"
                      displayField={true}
                      value={values.registration.tenant.stateOfFormation ?? ''}
                      disabled={true}
                    />
                  ) : (
                    <Field
                      component={Select}
                      label="State of Formation"
                      name="registration.tenant.stateOfFormation"
                      options={usStates}
                      required
                      disabled={isSubmitting || !canEditForm}
                    />
                  )}
                  {!lockAuthorizedEntityFields &&
                    getIn(touched, 'registration.tenant.stateOfFormation') === true &&
                    typeof getIn(errors, 'registration.tenant.stateOfFormation') === 'string' && (
                      <p className="mt-1 text-xs text-red-600">{getIn(errors, 'registration.tenant.stateOfFormation') as string}</p>
                    )}
                </div>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
                <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Leaseholder's Authorized Signatory</div>
                <div className="grid grid-cols-12 gap-y-4 gap-x-4">
                  <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                    <Field
                      as={Input}
                      label="First name"
                      name="registration.tenant.contact.firstName"
                      required
                      displayField={!canEditForm}
                      value={values.registration.tenant.contact.firstName}
                      disabled={isSubmitting}
                      error={
                        errors.registration?.tenant?.contact?.firstName !== undefined && (touched.registration?.tenant?.contact?.firstName ?? false)
                          ? errors.registration?.tenant?.contact?.firstName
                          : undefined
                      }
                    />
                  </div>
                  <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                    <Field
                      as={Input}
                      label="Last name"
                      name="registration.tenant.contact.lastName"
                      required
                      displayField={!canEditForm}
                      value={values.registration.tenant.contact.lastName}
                      disabled={isSubmitting}
                      error={
                        errors.registration?.tenant?.contact?.lastName !== undefined && (touched.registration?.tenant?.contact?.lastName ?? false)
                          ? errors.registration?.tenant?.contact?.lastName
                          : undefined
                      }
                    />
                  </div>
                  <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                    <Field
                      as={Input}
                      label="Title"
                      name="registration.tenant.contact.title"
                      displayField={!canEditForm}
                      onBlur={async (event: React.FocusEvent<HTMLInputElement>): Promise<void> => {
                        const name = 'registration.tenant.contact.title'
                        await setFieldValue(name, values.registration.tenant.contact.title, true)
                        setTimeout(() => {
                          void validateField(name)
                          handleBlur(event)
                        }, 0)
                      }}
                      value={values.registration.tenant.contact.title}
                      disabled={isSubmitting}
                      error={
                        errors.registration?.tenant?.contact?.title !== undefined && (touched.registration?.tenant?.contact?.title ?? false)
                          ? errors.registration?.tenant?.contact?.title
                          : undefined
                      }
                    />
                  </div>
                  <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100"></div>
                  <div className="col-span-6 flex flex-col text-slate-900 dark:text-slate-100">
                    <Field
                      as={Input}
                      label="Email address"
                      name="registration.tenant.contact.emailAddress"
                      required
                      displayField={!canEditForm}
                      value={values.registration.tenant.contact.emailAddress}
                      disabled={isSubmitting}
                      error={
                        errors.registration?.tenant?.contact?.emailAddress !== undefined && (touched.registration?.tenant?.contact?.emailAddress ?? false)
                          ? errors.registration?.tenant?.contact?.emailAddress
                          : undefined
                      }
                    />
                  </div>
                  <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                    <Field
                      as={Input}
                      label="Phone number"
                      name="registration.tenant.contact.phoneNumber"
                      required
                      displayField={!canEditForm}
                      onBlur={async (event: React.FocusEvent<HTMLInputElement>): Promise<void> => {
                        const name = 'registration.tenant.contact.phoneNumber'
                        await setFieldValue(name, formatPhoneNumber(values.registration.tenant.contact.phoneNumber), true)
                        setTimeout(() => {
                          void validateField(name)
                          handleBlur(event)
                        }, 0)
                      }}
                      value={values.registration.tenant.contact.phoneNumber}
                      disabled={isSubmitting}
                      error={
                        errors.registration?.tenant?.contact?.phoneNumber !== undefined && (touched.registration?.tenant?.contact?.phoneNumber ?? false)
                          ? errors.registration?.tenant?.contact?.phoneNumber
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </Panel>
          ) : (
            <Panel header="Contact">
              <div className="grid grid-cols-12 gap-y-4 gap-x-4">
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="First name"
                    name="registration.tenant.contact.firstName"
                    required
                    displayField={!canEditForm}
                    value={values.registration.tenant.contact.firstName}
                    disabled={isSubmitting}
                    error={
                      errors.registration?.tenant?.contact?.firstName !== undefined && (touched.registration?.tenant?.contact?.firstName ?? false)
                        ? errors.registration?.tenant?.contact?.firstName
                        : undefined
                    }
                  />
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Last name"
                    name="registration.tenant.contact.lastName"
                    required
                    displayField={!canEditForm}
                    value={values.registration.tenant.contact.lastName}
                    disabled={isSubmitting}
                    error={
                      errors.registration?.tenant?.contact?.lastName !== undefined && (touched.registration?.tenant?.contact?.lastName ?? false)
                        ? errors.registration?.tenant?.contact?.lastName
                        : undefined
                    }
                  />
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Title"
                    name="registration.tenant.contact.title"
                    displayField={!canEditForm}
                    value={values.registration.tenant.contact.title}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100"></div>
                <div className="col-span-6 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Email address"
                    name="registration.tenant.contact.emailAddress"
                    required
                    displayField={!canEditForm}
                    value={values.registration.tenant.contact.emailAddress}
                    disabled={isSubmitting}
                    error={
                      errors.registration?.tenant?.contact?.emailAddress !== undefined && (touched.registration?.tenant?.contact?.emailAddress ?? false)
                        ? errors.registration?.tenant?.contact?.emailAddress
                        : undefined
                    }
                  />
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Phone number"
                    name="registration.tenant.contact.phoneNumber"
                    required
                    displayField={!canEditForm}
                    onBlur={async (event: React.FocusEvent<HTMLInputElement>): Promise<void> => {
                      const name = 'registration.tenant.contact.phoneNumber'
                      await setFieldValue(name, formatPhoneNumber(values.registration.tenant.contact.phoneNumber), true)
                      setTimeout(() => {
                        void validateField(name)
                        handleBlur(event)
                      }, 0)
                    }}
                    value={values.registration.tenant.contact.phoneNumber}
                    disabled={isSubmitting}
                    error={
                      errors.registration?.tenant?.contact?.phoneNumber !== undefined && (touched.registration?.tenant?.contact?.phoneNumber ?? false)
                        ? errors.registration?.tenant?.contact?.phoneNumber
                        : undefined
                    }
                  />
                </div>
              </div>
            </Panel>
          )}
          <Panel
            header={
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                  <span className="mr-4 inline-flex flex-col justify-center text-md font-medium leading-4 shrink-0">{props.onboarding.utility} Utility Accounts:</span>
                  {accountOptions.map((account, index) => (
                    <a
                      key={index}
                      className={classnames(
                        account.value === accountOptions[values.selectedAccountIdx].value
                          ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300',
                        'whitespace-nowrap mx-2 p-1 border-b-2 font-medium text-sm inline-flex items-center gap-1'
                      )}
                      onClick={() => {
                        void setFieldValue('selectedAccountIdx', account.value)
                        void setFieldValue('selectedAgreementIdx', agreementOptions[0].value)
                      }}
                    >
                      UA {index + 1}
                      {uaHasErrors(index) ? (
                        <ExclamationTriangleIcon
                          className="w-4 h-4 text-red-600 dark:text-red-400"
                          aria-label="This utility account has validation errors"
                        />
                      ) : uaHasWarnings(index) ? (
                        <ExclamationTriangleIcon
                          className="w-4 h-4 text-amber-600 dark:text-amber-400"
                          aria-label="This utility account has format warnings"
                        />
                      ) : null}
                    </a>
                  ))}
                </div>
                {canAddRemoveUaSa && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      title="Add utility account"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800"
                      onClick={() => {
                        if (hasAnyPma) {
                          setShowAddUaConfirm(true)
                          return
                        }
                        const sourceUaIdx = Number(values.selectedAccountIdx)
                        const sourceProgramId =
                          values.registration.tenant.accounts[sourceUaIdx]?.agreements[0]?.salesforceProgramId ?? ''
                        const seededUa: UtilityAccount = {
                          ...blankUtilityAccount(),
                          agreements: [{ ...blankServiceAgreement(), salesforceProgramId: sourceProgramId }],
                        }
                        const newAccounts = [...values.registration.tenant.accounts, seededUa]
                        void setFieldValue('registration.tenant.accounts', newAccounts).then(() => {
                          void setFieldValue('selectedAccountIdx', `${newAccounts.length - 1}`)
                          void setFieldValue('selectedAgreementIdx', '0')
                        })
                      }}
                    >
                      <PlusIcon className="w-3.5 h-3.5" /> Add UA
                    </button>
                    {values.registration.tenant.accounts.length > 1 && (
                      <button
                        type="button"
                        title="Remove this utility account"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/40 border border-transparent hover:border-red-200 dark:hover:border-red-800"
                        onClick={() => setShowRemoveUaConfirm(true)}
                      >
                        <TrashIcon className="w-3.5 h-3.5" /> Remove UA
                      </button>
                    )}
                  </div>
                )}
              </div>
            }
          >
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Tenant Input</div>
            <div className="grid grid-cols-12 gap-y-4 gap-x-4">
              <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Utility Account Type"
                  name="registration.tenant.utilityAccountType"
                  displayField={true}
                  value={values.registration.tenant.utilityAccountType ?? 'Not set'}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Tax ID / TIN / EIN"
                  name="registration.tenant.ein"
                  displayField={true}
                  value={values.registration.tenant.ein ?? 'Not set'}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                <div className="flex flex-row items-center">
                  <Field
                    as={Input}
                    label="SSN Last Four"
                    name="registration.tenant.ssnLastFour"
                    displayField={true}
                    value={
                      values.registration.tenant.ssnLastFour == null
                        ? 'Not set'
                        : isSsnHidden || values.registration.tenant.ssnLastFour == 'VALUE_SET_INSUFFICIENT_PERMISSIONS'
                          ? '••••'
                          : values.registration.tenant.ssnLastFour
                    }
                    disabled={isSubmitting}
                  />
                  {values.registration.tenant.ssnLastFour != null &&
                    values.registration.tenant.ssnLastFour !== 'VALUE_SET_INSUFFICIENT_PERMISSIONS' &&
                    (isSsnHidden ? (
                      <EyeSlashIcon className="w-4 h-4 inline-block cursor-pointer" onClick={() => setIsSsnHidden(false)} />
                    ) : (
                      <EyeIcon className="w-4 h-4 inline-block cursor-pointer" onClick={() => setIsSsnHidden(true)} />
                    ))}
                </div>
              </div>
            </div>
            {utilityAccountIdsUserInput.length > 0 &&
              (utilityAccountIdsUserInput.some((id: string) => !invitationAccountNumbers.includes(id)) ||
                invitationAccountNumbers.some((num: string) => !utilityAccountIdsUserInput.includes(num))) && (
                <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
                  <div className="text-sm font-medium text-slate-500 dark:text-slate-400">Utility Account Numbers Authorized by Tenant</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    These modifications are reflected in the Utility Authorization (PMA/CISR).
                  </div>
                  <div className="grid grid-cols-12 gap-y-2 gap-x-4">
                    {utilityAccountIdsUserInput.map((userInputId: string, idx: number) => {
                      const matchesInvitation = invitationAccountNumbers.includes(userInputId)
                      return (
                        <div key={`added-${idx}`} className="col-span-3 flex items-center gap-2 text-sm text-slate-900 dark:text-slate-100">
                          <span className="font-mono">{userInputId}</span>
                          {!matchesInvitation && (
                            <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 rounded text-xs">Added</span>
                          )}
                        </div>
                      )
                    })}
                    {invitationAccountNumbers
                      .filter((num: string) => !utilityAccountIdsUserInput.includes(num))
                      .map((removedId: string, idx: number) => (
                        <div key={`removed-${idx}`} className="col-span-3 flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500">
                          <span className="font-mono line-through">{removedId}</span>
                          <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded text-xs">Removed</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4"></div>
            <div className="grid grid-cols-12 gap-y-4 gap-x-4">
              <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Utility account number"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].number`}
                  required
                  type="text"
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].number}
                  disabled={isSubmitting}
                  error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].number`)}
                />
                {(() => {
                  const w = getUtilityAccountNumberWarning(
                    values.registration.tenant.accounts[values.selectedAccountIdx].number,
                    values.registration.utility
                  )
                  return w && !fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].number`) ? (
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{w}</p>
                  ) : null
                })()}
              </div>
              <div className="col-span-3 dark:text-slate-100"></div>
              <div className="col-span-3 col-start-7 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Billing address"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.address`}
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].billingAddress.address}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Suite"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.address2`}
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].billingAddress.address2 ?? ''}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-span-6 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Utility account name"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].name`}
                  required
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].name}
                  disabled={isSubmitting}
                  error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].name`)}
                />
              </div>
              <div className="col-span-2 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="City"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.city`}
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].billingAddress.city}
                  disabled={isSubmitting}
                />
              </div>
              <div className="col-span-2 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="State"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.state`}
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].billingAddress.state}
                  disabled={isSubmitting}
                  error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.state`)}
                />
              </div>
              <div className="col-span-2 flex flex-col text-slate-900 dark:text-slate-100">
                <Field
                  as={Input}
                  label="Zip"
                  name={`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.zipCode`}
                  displayField={!canEditForm}
                  className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                  value={values.registration.tenant.accounts[values.selectedAccountIdx].billingAddress.zipCode}
                  disabled={isSubmitting}
                  error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].billingAddress.zipCode`)}
                />
              </div>
            </div>

            <div className="mt-4 p-4 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/70">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                  <span className="mr-4 inline-flex flex-col justify-center text-md font-medium leading-4 text-slate-700 dark:text-slate-300 shrink-0">
                    Service Agreements:
                  </span>
                  {agreementOptions.map((account, index) => (
                    <a
                      key={index}
                      className={classnames(
                        account.value === agreementOptions[values.selectedAgreementIdx].value
                          ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                          : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300',
                        'whitespace-nowrap mx-2 p-1 border-b-2 font-medium text-sm inline-flex items-center gap-1'
                      )}
                      onClick={() => {
                        void setFieldValue('selectedAgreementIdx', account.value)
                      }}
                    >
                      SA {Number(values.selectedAccountIdx) + 1}-{index + 1}
                      {saHasErrors(Number(values.selectedAccountIdx), index) ? (
                        <ExclamationTriangleIcon
                          className="w-4 h-4 text-red-600 dark:text-red-400"
                          aria-label="This service agreement has validation errors"
                        />
                      ) : saHasWarnings(Number(values.selectedAccountIdx), index) ? (
                        <ExclamationTriangleIcon
                          className="w-4 h-4 text-amber-600 dark:text-amber-400"
                          aria-label="This service agreement has format warnings"
                        />
                      ) : null}
                    </a>
                  ))}
                </div>
                {canAddRemoveUaSa && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      title="Add service agreement"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800"
                      onClick={() => {
                        if (hasAnyPma) {
                          setShowAddSaConfirm(true)
                          return
                        }
                        const uaIdx = Number(values.selectedAccountIdx)
                        const currentAgreements = values.registration.tenant.accounts[uaIdx].agreements ?? []
                        const seededSa = {
                          ...blankServiceAgreement(),
                          salesforceProgramId: currentAgreements[0]?.salesforceProgramId ?? '',
                        }
                        const newAgreements = [...currentAgreements, seededSa]
                        void setFieldValue(
                          `registration.tenant.accounts[${uaIdx}].agreements`,
                          newAgreements
                        ).then(() => {
                          void setFieldValue('selectedAgreementIdx', `${newAgreements.length - 1}`)
                        })
                      }}
                    >
                      <PlusIcon className="w-3.5 h-3.5" /> Add SA
                    </button>
                    {(values.registration.tenant.accounts[values.selectedAccountIdx].agreements?.length ?? 0) > 1 && (
                      <button
                        type="button"
                        title="Remove this service agreement"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/40 border border-transparent hover:border-red-200 dark:hover:border-red-800"
                        onClick={() => setShowRemoveSaConfirm(true)}
                      >
                        <TrashIcon className="w-3.5 h-3.5" /> Remove SA
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-12 gap-y-4 gap-x-4">
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Service agreement number"
                    name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].number`}
                    required
                    displayField={!canEditForm}
                    className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                    value={values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].number}
                    disabled={isSubmitting}
                    error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].number`)}
                  />
                  {(() => {
                    const path = `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].number`
                    const w = getServiceAgreementNumberWarning(
                      values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].number,
                      values.registration.utility
                    )
                    return w && !fieldError(path) ? (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{w}</p>
                    ) : null
                  })()}
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Meter number"
                    name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].meterNumber`}
                    required
                    displayField={!canEditForm}
                    className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                    value={values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].meterNumber}
                    disabled={isSubmitting}
                    error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].meterNumber`)}
                  />
                  {(() => {
                    const path = `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].meterNumber`
                    const w = getMeterNumberWarning(
                      values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].meterNumber,
                      values.registration.utility
                    )
                    return w && !fieldError(path) ? (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{w}</p>
                    ) : null
                  })()}
                </div>
                <div className="col-span-6 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Service address"
                    name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.address`}
                    required
                    displayField={!canEditForm}
                    className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                    value={values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].serviceAddress.address}
                    disabled={isSubmitting}
                    error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.address`)}
                  />
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  {(() => {
                    const tariffFieldPath = `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].tariffSchedule`
                    const currentTariff =
                      values.registration.tenant.accounts[values.selectedAccountIdx].agreements[
                        values.selectedAgreementIdx
                      ].tariffSchedule ?? ''
                    return (
                      <Field
                        as={Input}
                        label="Tariff schedule"
                        name={tariffFieldPath}
                        required={!isNgomAccount}
                        displayField={!canEditForm}
                        className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                        value={currentTariff}
                        disabled={isSubmitting}
                        error={fieldError(tariffFieldPath)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          void setFieldValue(tariffFieldPath, e.target.value.toUpperCase())
                        }}
                      />
                    )
                  })()}
                  {(() => {
                    const tariffFieldPath = `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].tariffSchedule`
                    const currentTariff =
                      values.registration.tenant.accounts[values.selectedAccountIdx].agreements[
                        values.selectedAgreementIdx
                      ].tariffSchedule ?? ''
                    const warning = getTariffScheduleWarning(currentTariff, knownTariffs, values.registration.utility)
                    if (!warning) return null
                    return (
                      <p className="mt-1 text-xs text-left text-amber-700 dark:text-amber-400">
                        {warning.message}.{' '}
                        {warning.suggestion != null && (
                          <>
                            Did you mean{' '}
                            <button
                              type="button"
                              className="font-semibold underline hover:no-underline"
                              onClick={() => void setFieldValue(tariffFieldPath, warning.suggestion)}
                            >
                              {warning.suggestion}
                            </button>
                            ?
                          </>
                        )}
                      </p>
                    )
                  })()}
                </div>
                <div className="col-span-3"></div>
                <div className="col-span-2 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="City"
                    name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.city`}
                    required
                    displayField={!canEditForm}
                    className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                    value={values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].serviceAddress.city}
                    disabled={isSubmitting}
                    error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.city`)}
                  />
                </div>
                <div className="col-span-2 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="State"
                    name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.state`}
                    required
                    displayField={!canEditForm}
                    className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                    value={values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].serviceAddress.state}
                    disabled={isSubmitting}
                    error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.state`)}
                  />
                </div>
                <div className="col-span-2 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field
                    as={Input}
                    label="Zip"
                    name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.zipCode`}
                    required
                    displayField={!canEditForm}
                    className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                    value={values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].serviceAddress.zipCode}
                    disabled={isSubmitting}
                    error={fieldError(`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].serviceAddress.zipCode`)}
                  />
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].salesforceProgramId`}>
                    {({ field }: { field: { name: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                      <Input
                        label="SF Program ID"
                        name={field.name}
                        displayField={!canEditForm}
                        className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                        value={field.value ?? ''}
                        disabled={isSubmitting}
                        error={fieldError(field.name)}
                        onChange={(e) => {
                          field.onChange(e)
                          setSfProgramNameMissing(false)
                          setFieldValue(
                            `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].salesforceProgramName`,
                            null
                          )
                        }}
                        onBlur={(e) => {
                          field.onBlur(e)
                          void refreshAgreementSalesforceName(
                            'program',
                            e.target.value,
                            values.selectedAccountIdx,
                            values.selectedAgreementIdx
                          )
                        }}
                      />
                    )}
                  </Field>
                  {(() => {
                    const path = `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].salesforceProgramId`
                    const w = getSalesforceIdWarning(
                      values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].salesforceProgramId
                    )
                    if (w && !fieldError(path)) {
                      return <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{w}</p>
                    }
                    if (sfProgramNameMissing && !fieldError(path)) {
                      return (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          Salesforce record not found
                        </p>
                      )
                    }
                    return null
                  })()}
                  {!!values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].salesforceProgramId && (
                    <a
                      href={`${SALESFORCE_BASE_URL}/lightning/r/Program__c/${values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].salesforceProgramId}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={
                        values.registration.tenant.accounts[values.selectedAccountIdx].agreements[
                          values.selectedAgreementIdx
                        ].salesforceProgramId ?? undefined
                      }
                      className="mt-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline truncate"
                    >
                      {values.registration.tenant.accounts[values.selectedAccountIdx].agreements[
                        values.selectedAgreementIdx
                      ].salesforceProgramName || 'Open in Salesforce'}
                    </a>
                  )}
                </div>
                <div className="col-span-3 flex flex-col text-slate-900 dark:text-slate-100">
                  <Field name={`registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].salesforceMeterId`}>
                    {({ field }: { field: { name: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; onBlur: (e: React.FocusEvent<HTMLInputElement>) => void } }) => (
                      <Input
                        label="SF Meter Data ID"
                        name={field.name}
                        displayField={!canEditForm}
                        className="mt-1 text-sm rounded-md border-transparent focus:border-slate-500 dark:focus:border-slate-400 focus:bg-white"
                        value={field.value ?? ''}
                        disabled={isSubmitting}
                        error={fieldError(field.name)}
                        onChange={(e) => {
                          field.onChange(e)
                          setSfMeterNameMissing(false)
                          setFieldValue(
                            `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].salesforceMeterName`,
                            null
                          )
                        }}
                        onBlur={(e) => {
                          field.onBlur(e)
                          void refreshAgreementSalesforceName(
                            'meter',
                            e.target.value,
                            values.selectedAccountIdx,
                            values.selectedAgreementIdx
                          )
                        }}
                      />
                    )}
                  </Field>
                  {(() => {
                    const path = `registration.tenant.accounts[${values.selectedAccountIdx}].agreements[${values.selectedAgreementIdx}].salesforceMeterId`
                    const w = getSalesforceIdWarning(
                      values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].salesforceMeterId
                    )
                    if (w && !fieldError(path)) {
                      return <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{w}</p>
                    }
                    if (sfMeterNameMissing && !fieldError(path)) {
                      return (
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          Salesforce record not found
                        </p>
                      )
                    }
                    return null
                  })()}
                  {!!values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].salesforceMeterId && (
                    <a
                      href={`${SALESFORCE_BASE_URL}/lightning/r/Meter_Data__c/${values.registration.tenant.accounts[values.selectedAccountIdx].agreements[values.selectedAgreementIdx].salesforceMeterId}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={
                        values.registration.tenant.accounts[values.selectedAccountIdx].agreements[
                          values.selectedAgreementIdx
                        ].salesforceMeterId ?? undefined
                      }
                      className="mt-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline truncate"
                    >
                      {values.registration.tenant.accounts[values.selectedAccountIdx].agreements[
                        values.selectedAgreementIdx
                      ].salesforceMeterName || 'Open in Salesforce'}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </Panel>

          {!isNgomAccount ? (
          <Panel header="Signed Documents">
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">TESA Click-To-Accept</div>
            {tesaAgreement && tesaAcceptedAt ? (
              <div className="grid grid-cols-[auto_auto_auto] gap-2 items-center text-sm">
                <div className="font-medium">Version</div>
                <div className="font-medium">Accepted</div>
                <div className="font-medium">Actions</div>
                <div>
                  {tesaVersion}
                  {parsedTesa.isLegacy ? (
                    <span className="ml-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 rounded text-xs">Legacy</span>
                  ) : parsedTesa.isCurrent ? (
                    <span className="ml-1 px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-xs">Current</span>
                  ) : null}
                </div>
                <div>{format(new Date(tesaAcceptedAt), "M/d/yy 'at' h:mm aaa")}</div>
                <div className="flex flex-row">
                  {!canShowOnboardingDocumentViewAction({ ...documentViewActionBase, isTesaClickToAccept: true }) ? (
                    <span className="text-xs text-slate-400 dark:text-slate-500 italic max-w-xs">
                      {TENANT_NOT_CREATED_DOCUMENT_MESSAGE}
                    </span>
                  ) : (
                    <Link to={`/tenant/onboarding/TESA/${tesaAgreement.cuid}`} target="_blank" rel="noopener noreferrer" tabIndex={-1}>
                      <Button size={1} color="slate" disabled={isSubmitting} tabIndex={-1}>
                        <span>View document</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 inline align-text-bottom ml-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400 dark:text-slate-500 italic">No documents</div>
            )}
            <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4"></div>
            <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Utility Authorization (PMA/CISR) Click-To-Accept</div>
            {tesaSupplements.length > 0 ? (
              <div className="grid grid-cols-[auto_auto_auto_auto_auto] gap-2 items-center text-sm">
                <div className="font-medium">Status</div>
                <div className="font-medium">Timestamp</div>
                <div className="font-medium">Expires</div>
                <div className="font-medium">Reference ID</div>
                <div className="font-medium">Actions</div>
                {tesaSupplements.map((as: AgreementSubmission, index: number) => (
                  <div className="contents" key={index}>
                    <div>
                      {(as.status === 'SUBMITTED' || as.status === 'EXPIRED' || as.status === 'EXPIRING_SOON') && as.signatureSource !== 'EXTERNAL' && (
                        <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                          Submitted to utility
                        </span>
                      )}
                      {(as.status === 'SUBMITTED' || as.status === 'EXPIRED' || as.status === 'EXPIRING_SOON') && as.signatureSource === 'EXTERNAL' && (
                        <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
                          Uploaded document
                        </span>
                      )}
                      {as.status === 'ACCEPTED' && (
                        <span className="inline-flex items-center rounded-md bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                          Accepted
                        </span>
                      )}
                      {as.status === 'REJECTED' && (
                        <span className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-300">
                          Rejected
                        </span>
                      )}
                    </div>
                    <div>{format(new Date(as.statusDate), "M/d/yy 'at' h:mm aaa")}</div>
                    <div className="flex items-center gap-1.5">
                      {as.expiresAt ? (
                        (() => {
                          const expiresDate = new Date(as.expiresAt)
                          const now = new Date()
                          const daysUntil = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
                          const isExpired = daysUntil < 0
                          const isExpiring = !isExpired && daysUntil <= 60
                          return (
                            <>
                              <span
                                className={classnames('text-sm', {
                                  'text-red-600 dark:text-red-400 font-medium': isExpired,
                                  'text-amber-600 dark:text-amber-400 font-medium': isExpiring,
                                  'text-slate-500 dark:text-slate-400': !isExpired && !isExpiring,
                                })}
                              >
                                {format(expiresDate, 'M/d/yy')}
                              </span>
                              {isExpired && (
                                <span className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:text-red-300">
                                  Expired
                                </span>
                              )}
                              {isExpiring && (
                                <span className="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                                  Expiring
                                </span>
                              )}
                            </>
                          )
                        })()
                      ) : (
                        <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </div>
                    <div>{as.referenceId.split('-')[0]}</div>
                    <div className="flex flex-row">
                      {!canShowOnboardingDocumentViewAction({
                        ...documentViewActionBase,
                        signatureSource: as.signatureSource,
                      }) ? (
                        <span className="text-xs text-slate-400 dark:text-slate-500 italic max-w-xs mr-2">
                          {TENANT_NOT_CREATED_DOCUMENT_MESSAGE}
                        </span>
                      ) : (
                        <Link to={`/tenant/onboarding/TESAsupplement/${as.agreementId}`} target="_blank" rel="noopener noreferrer" tabIndex={-1} className="mr-2">
                          <Button color="slate" className="mr-2" disabled={isSubmitting} size={1} tabIndex={-1}>
                            <span>View document</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 inline align-text-bottom ml-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </Button>
                        </Link>
                      )}
                      {as.status === 'SUBMITTED' && (
                        <>
                          <Button
                            size={1}
                            color="emerald"
                            className="mr-2"
                            disabled={isSubmitting}
                            tabIndex={-1}
                            onClick={async () => {
                              try {
                                await updateTESASupplementAgreementStatusApi(as.agreementId, 'ACCEPTED')
                                await props.refreshList()
                                dispatch(
                                  pushToast({
                                    type: 'success',
                                    message: 'Utility authorization agreement accepted on behalf of utility',
                                  })
                                )
                              } catch (error: any) {
                                dispatch(
                                  pushToast({
                                    type: 'error',
                                    message: 'Failed to accept utility authorization agreement',
                                    description: error.response?.data?.message ?? error.response?.message ?? error.message,
                                  })
                                )
                              }
                            }}
                          >
                            <span>Accept</span>
                          </Button>
                          <Button
                            size={1}
                            color="red"
                            className="mr-2"
                            disabled={isSubmitting}
                            tabIndex={-1}
                            onClick={async () => {
                              try {
                                await updateTESASupplementAgreementStatusApi(as.agreementId, 'REJECTED')
                                await props.refreshList()
                                dispatch(
                                  pushToast({
                                    type: 'success',
                                    message: 'Utility authorization agreement rejected on behalf of utility',
                                  })
                                )
                              } catch (error: any) {
                                dispatch(
                                  pushToast({
                                    type: 'error',
                                    message: 'Failed to reject utility authorization agreement',
                                    description: error.response?.data?.message ?? error.response?.message ?? error.message,
                                  })
                                )
                              }
                            }}
                          >
                            <span>Reject</span>
                          </Button>
                        </>
                      )}
                      {(as.status === 'ACCEPTED' || as.status === 'EXPIRING_SOON' || as.status === 'EXPIRED') && hasAgreementUpdatePermission && (
                        <Button
                          size={1}
                          color="red"
                          className="mr-2"
                          disabled={isSubmitting}
                          tabIndex={-1}
                          onClick={async () => {
                            try {
                              await updateTESASupplementAgreementStatusApi(as.agreementId, 'REJECTED')
                              await props.refreshList()
                              dispatch(
                                pushToast({
                                  type: 'success',
                                  message: 'Utility authorization agreement rejected - tenant can now re-submit',
                                })
                              )
                            } catch (error: any) {
                              dispatch(
                                pushToast({
                                  type: 'error',
                                  message: 'Failed to reject utility authorization agreement',
                                  description: error.response?.data?.message ?? error.response?.message ?? error.message,
                                })
                              )
                            }
                          }}
                        >
                          <span>Reject</span>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-400 dark:text-slate-500 italic">No documents</div>
            )}
            <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
              <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">Externally Signed Documents</div>
              {externallySignedAgreementDocs.length > 0 ? (
                <div className="grid grid-cols-[auto_auto_auto] gap-2 items-center text-sm">
                  <div className="font-medium">Type</div>
                  <div className="font-medium">Date Signed</div>
                  <div className="font-medium">Actions</div>
                  {externallySignedAgreementDocs.map((doc: ExternallySignedAgreementDocument, index: number) => (
                    <div className="contents" key={index}>
                      <div className="h-4 flex items-center gap-1.5">
                        {doc.type === 'TESA_SUPPLEMENT' ? 'Utility Authorization' : formatTitleCase(doc.type).replace(new RegExp('Tesa', 'g'), 'TESA')}
                        {doc.type === 'TESA' && tesaVersion && (
                          <span
                            className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              parsedTesa.isCustom
                                ? 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                                : parsedTesa.isLegacy
                                  ? 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200'
                                  : parsedTesa.isCurrent
                                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                    : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                            }`}
                          >
                            {parsedTesa.isCustom ? 'Custom' : parsedTesa.display}
                          </span>
                        )}
                      </div>
                      <div>{formatUTCDate(doc.dateSigned)}</div>
                      <div className="flex flex-row">
                        {!canShowOnboardingDocumentViewAction({
                          ...documentViewActionBase,
                          isExternallyStoredDocument: true,
                        }) ? (
                          <span className="text-xs text-slate-400 dark:text-slate-500 italic max-w-xs mr-2">
                            {TENANT_NOT_CREATED_DOCUMENT_MESSAGE}
                          </span>
                        ) : (
                          <Link
                            to={`/tenant/onboarding/${doc.type === 'TESA_SUPPLEMENT' ? 'TESAsupplement' : 'TESA'}/${doc.agreementId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex={-1}
                            className="mr-2"
                          >
                            <Button size={1} color="slate" className="mr-2" disabled={isSubmitting} tabIndex={-1}>
                              <span>View document</span>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 inline align-text-bottom ml-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </Button>
                          </Link>
                        )}
                        <div>
                          <Button
                            size={1}
                            color="slate"
                            className="mr-2"
                            disabled={isSubmitting}
                            tabIndex={-1}
                            onClick={() => {
                              setShowAgreementDocTypeOptions(false)
                              setSelectedAgreementDocRefIndex(index)
                              setAgreementDocType({ label: doc.type === 'TESA_SUPPLEMENT' ? 'Utility Authorization' : 'TESA', value: doc.type })
                              handleUploadClick(false)
                            }}
                          >
                            <span>Change document...</span>
                          </Button>
                          <input
                            type="file"
                            ref={(el) => {
                              if (el != null) {
                                changeDocInputRefs.current[index] = el
                              }
                            }}
                            onChange={(event) => handleFileChange(event, false, doc.type, doc.agreementId)}
                            style={{ display: 'none' }}
                            accept="application/pdf"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-slate-400 dark:text-slate-500 italic">No documents</div>
              )}
            </div>
            <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
              <Button
                onClick={() => {
                  setShowAgreementDocTypeOptions(true)
                  handleUploadClick(true)
                }}
              >
                Upload externally signed document...
              </Button>
              <input
                type="file"
                ref={uploadNewDocInputRef}
                onChange={(event) => handleFileChange(event, true, agreementDocType.value)}
                style={{ display: 'none' }}
                accept="application/pdf"
              />
              <CustomContentModal isOpen={showUploadDateModal} onClose={() => {}}>
                <div className="mb-3 bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-left">
                  {showAgreementDocTypeOptions && (
                    <ComboboxAutocomplete
                      label="Agreement type"
                      wildCardDisabled={true}
                      value={agreementDocType.value}
                      data={agreementDocTypes}
                      handleChange={(selected) => {
                        setAgreementDocType(
                          agreementDocTypes.find((i) => i.id === selected)
                            ? {
                                label: agreementDocTypes.find((i) => i.id === selected)!.name,
                                value: agreementDocTypes.find((i) => i.id === selected)!.id,
                              }
                            : { label: 'Utility Authorization', value: 'TESA_SUPPLEMENT' }
                        )
                      }}
                    />
                  )}
                  <div className="mt-4">
                    <label className="mt-4 text-sm">Date signed</label>
                    <Datepicker
                      useRange={false}
                      asSingle={true}
                      value={uploadedDate}
                      onChange={(date: any) => {
                        setUploadedDate(date)
                      }}
                      primaryColor={'indigo'}
                      inputClassName={classnames(getInputStyles(), 'inline-block')}
                      popoverDirection="down"
                      displayFormat={'MM/DD/YYYY'}
                      maxDate={new Date()}
                    />
                  </div>
                  {agreementDocType.value === 'TESA' && (
                    <div className="mt-4">
                      <label className="text-sm">TESA version</label>
                      <select
                        value={uploadTesaVersion}
                        onChange={(e) => setUploadTesaVersion(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:ring-indigo-500"
                      >
                          <option value={`v${LATEST_TESA_VERSION}`}>{`v${LATEST_TESA_VERSION} (current)`}</option>
                          <option value="v6">v6</option>
                          <option value="v5">v5</option>
                          <option value="v4">v4</option>
                          <option value="v3">v3</option>
                          <option value="v2">v2</option>
                          <option value="v1">v1</option>
                          <option value="custom">Custom</option>
                      </select>
                    </div>
                  )}
                  {agreementDocType.value === 'TESA_SUPPLEMENT' && (
                    <div className="mt-4">
                      <label className="text-sm">Expiration date</label>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <Datepicker
                            useRange={false}
                            asSingle={true}
                            value={uploadExpiresAt}
                            onChange={(date: any) => {
                              setUploadExpiresAt(date)
                              if (date?.startDate != null) setUploadExpiresIndefinite(false)
                            }}
                            primaryColor={'indigo'}
                            inputClassName={classnames(getInputStyles(), 'inline-block')}
                            popoverDirection="down"
                            displayFormat={'MM/DD/YYYY'}
                            minDate={new Date()}
                            disabled={uploadExpiresIndefinite}
                          />
                        </div>
                        <label className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={uploadExpiresIndefinite}
                            onChange={(e) => {
                              setUploadExpiresIndefinite(e.target.checked)
                              if (e.target.checked) setUploadExpiresAt({ startDate: null, endDate: null })
                            }}
                            className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                          />
                          Indefinite
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end mt-4">
                    <Button
                      onClick={() => {
                        setShowUploadDateModal(false)
                      }}
                      type="button"
                      color="slate"
                      className="w-28"
                    >
                      Close
                    </Button>
                    <Button
                      onClick={() => {
                        setShowUploadDateModal(false)
                        if (newUpload === true && uploadNewDocInputRef.current != null) {
                          uploadNewDocInputRef.current.click()
                        } else if (newUpload === false && changeDocInputRefs.current != null && selectedAgreementDocRefIndex != null) {
                          changeDocInputRefs.current[selectedAgreementDocRefIndex].click()
                        }
                      }}
                      type="button"
                      className="w-28 ml-2"
                    >
                      Upload...
                    </Button>
                  </div>
                </div>
              </CustomContentModal>
            </div>
          </Panel>
          ) : null}
          {props.onboarding.utility === 'PG&E' ? (
            <Panel header="PG&E Options">
              <div className="relative flex items-start">
                <div className="flex h-6 items-center">
                  <Field
                    name="registration.tenant.authorizeSpecificAccountsOnly"
                    type="checkbox"
                    id="authorizeSpecificAccountsOnly"
                    checked={values.registration.tenant.authorizeSpecificAccountsOnly ?? false}
                    disabled={isAccountInviteConsumed || isSubmitting}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
                <div className="ml-3 text-sm leading-6">
                  <label htmlFor="authorizeSpecificAccountsOnly" className="font-medium text-gray-900 dark:text-gray-100">
                    Authorize specific accounts only
                    <p id="comments-description" className="text-gray-500 dark:text-gray-400 text-xs">
                      Forces PMA form option &ldquo;Authorize management of specific accounts only&rdquo;
                      <br />
                      instead of &ldquo;Authorize management of ALL EXISTING AND FUTURE accounts&rdquo;
                    </p>
                  </label>
                </div>
              </div>
            </Panel>
          ) : null}
          <div className="py-4 flex flex-col gap-3 rounded-none text-right sticky bottom-0 bg-slate-100 dark:bg-slate-900">
            {showFormErrorSummary ? (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                Please fix the highlighted fields before saving.
              </p>
            ) : null}
            <div className="flex items-center gap-4">
              <div className="flex flex-1 justify-start items-end gap-4 min-w-0">
                {/* NGOM records have no TESA / PMA / OneBill flow, so neither the OnReg nor OneBill invitation links are meaningful — hide them entirely. */}
                {!isNgomAccount && (showOnregInvitationRow || showOnebillInvitationRow) ? (
                  <div className="flex flex-col gap-2 text-left flex-1 min-w-0">
                    {showOnregInvitationRow ? <InvitationLinkRow label="OnReg Invitation" url={onregUrl} /> : null}
                    {showOnebillInvitationRow ? <InvitationLinkRow label="OneBill Invitation" url={onebillUrl} /> : null}
                  </div>
                ) : null}
                {canArchive && (
                  <Button color="red" disabled={isSubmitting} tabIndex={-1} onClick={() => setShowArchiveConfirm(true)}>
                    Archive
                  </Button>
                )}
              </div>
              <div className="flex flex-1 justify-end">
              {hasLinkedTenant && (
                <Link to={`/tenant/tenants/${values.registration.tenantId}`} target="_blank" rel="noopener noreferrer" tabIndex={-1}>
                  <Button className="mr-4" disabled={isSubmitting} tabIndex={-1}>
                    <span>View tenant record</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 inline align-text-bottom ml-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </Button>
                </Link>
                )}
              {!isArchived && isOnregInviteConsumed && !isAccountInviteCreated && !hasLinkedTenant && (
                  <Button
                    className="mr-4"
                    disabled={isFetchingApi}
                    isSaving={isFetchingApi}
                    tabIndex={-1}
                    onClick={() => void handleCreateTenantClick()}
                  >
                    Create Tenant
                  </Button>
                )}
              {showMarkCompleteButton ? (
                <Button
                  type="button"
                    className="mr-4"
                  disabled={isSubmitting || isFetchingApi}
                  isSaving={isFetchingApi}
                  tabIndex={-1}
                  onClick={() => void handleMarkRegistrationComplete()}
                >
                  Mark registration complete
                </Button>
                ) : null}
              {canEditForm && (
                <Button type="submit" isSaving={isSubmitting} tabIndex={-1} className="w-28 ml-2">
                  Save
                </Button>
              )}
            </div>
            </div>
          </div>
        </form>
      </FormikProvider>
      <ConfirmDialogShell isOpen={showGenerateInviteConfirm}>
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 shrink-0">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Create tenant and generate OneBill account invitation?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This action will create tenant account(s) for every utility account on this onboarding record
              {!isNgomAccount && 'and generate a OneBill account-signup invitation link. This action is not easily reversible'}. 
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Please verify the onboarding record details before proceeding.
            </p>
          </div>
        </div>
        {/* Agreement-exempt records (NGOM/VNEM, National) don't require a PMA. */}
        <CreateTenantActionWarnings
          pmaWarningKind={pmaWarningKind}
          recordHasFormatWarnings={recordHasFormatWarnings}
          isAgreementExempt={isAgreementExempt}
        />
        <div className="text-sm border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
          <div className="flex justify-between items-start px-4 py-2 gap-4">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">Tenant</span>
            <span className="font-medium dark:text-gray-200 text-right">
              {values.registration.tenant.businessEntityName || values.registration.tenant.name || '(unnamed)'}
            </span>
          </div>
          <div className="flex justify-between px-4 py-2">
            <span className="text-gray-500 dark:text-gray-400">Utility accounts</span>
            <span className="font-medium dark:text-gray-200">{values.registration.tenant.accounts?.length ?? 0}</span>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button color="slate" onClick={() => {
            setShowGenerateInviteConfirm(false)
            setCreateDespiteConflicts(false)
          }} disabled={isFetchingApi}>
            Go back
          </Button>
          <Button
            isSaving={isFetchingApi}
            disabled={isFetchingApi}
            onClick={() => void runCreateTenant()}
          >
            {isFetchingApi ? 'Creating...' : 'Create Tenant'}
          </Button>
        </div>
      </ConfirmDialogShell>
      <CreateTenantConflictModal
        isOpen={showCreateTenantConflict}
        preview={createTenantPreview}
        recordSummary={{
          businessName: values.registration.tenant.name || '(unnamed)',
          property: values.registration.property,
          accounts: (values.registration.tenant.accounts ?? []).map((account) => ({
            name: account.name,
            number: account.number,
            agreements: (account.agreements ?? []).map((agreement) => ({
              serviceAgreementId: agreement.number,
              meterNumber: agreement.meterNumber,
            })),
          })),
        }}
        pmaWarningKind={pmaWarningKind}
        recordHasFormatWarnings={recordHasFormatWarnings}
        isAgreementExempt={isAgreementExempt}
        isSaving={isFetchingApi}
        onCancel={() => {
          setShowCreateTenantConflict(false)
          setCreateTenantPreview(null)
          setCreateDespiteConflicts(false)
        }}
        onMerge={(targetTenantAccountCuid) => void handleMergeIntoExistingTenant(targetTenantAccountCuid)}
        onCreateAnyway={() => {
          setCreateDespiteConflicts(true)
          setShowCreateTenantConflict(false)
          setShowGenerateInviteConfirm(true)
        }}
      />
      <ConfirmDialogShell isOpen={showAddUaConfirm}>
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 shrink-0">
            <PlusIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add a utility account?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This will add a new utility account to this onboarding record. Changes take effect after you save.
            </p>
          </div>
        </div>
        {hasAnyPma && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 dark:text-amber-100">
              The tenant may need to re-authorize the updated list on a new PMA.
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button color="slate" onClick={() => setShowAddUaConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const sourceUaIdx = Number(values.selectedAccountIdx)
              const sourceProgramId =
                values.registration.tenant.accounts[sourceUaIdx]?.agreements[0]?.salesforceProgramId ?? ''
              const seededUa: UtilityAccount = {
                ...blankUtilityAccount(),
                agreements: [{ ...blankServiceAgreement(), salesforceProgramId: sourceProgramId }],
              }
              const newAccounts = [...values.registration.tenant.accounts, seededUa]
              void formik.setValues({
                ...values,
                registration: {
                  ...values.registration,
                  tenant: { ...values.registration.tenant, accounts: newAccounts },
                },
                selectedAccountIdx: `${newAccounts.length - 1}` as unknown as number,
                selectedAgreementIdx: '0' as unknown as number,
              })
              setShowAddUaConfirm(false)
            }}
          >
            Add UA
          </Button>
        </div>
      </ConfirmDialogShell>
      <ConfirmDialogShell isOpen={showAddSaConfirm}>
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 shrink-0">
            <PlusIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add a service agreement?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This will add a new service agreement to UA {Number(values.selectedAccountIdx) + 1}. Changes take effect after you save.
            </p>
          </div>
        </div>
        {hasAnyPma && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 dark:text-amber-100">
              The tenant may need to re-authorize the updated list on a new PMA.
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button color="slate" onClick={() => setShowAddSaConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              const uaIdx = Number(values.selectedAccountIdx)
              const currentAgreements = values.registration.tenant.accounts[uaIdx]?.agreements ?? []
              const seededSa = {
                ...blankServiceAgreement(),
                salesforceProgramId: currentAgreements[0]?.salesforceProgramId ?? '',
              }
              const newAgreements = [...currentAgreements, seededSa]
              const newAccounts = values.registration.tenant.accounts.map((acct, i) =>
                i === uaIdx ? { ...acct, agreements: newAgreements } : acct,
              )
              void formik.setValues({
                ...values,
                registration: {
                  ...values.registration,
                  tenant: { ...values.registration.tenant, accounts: newAccounts },
                },
                selectedAgreementIdx: `${newAgreements.length - 1}` as unknown as number,
              })
              setShowAddSaConfirm(false)
            }}
          >
            Add SA
          </Button>
        </div>
      </ConfirmDialogShell>
      <ConfirmDialogShell isOpen={showRemoveUaConfirm}>
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 shrink-0">
            <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Remove Utility Account UA {Number(values.selectedAccountIdx) + 1}?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This will remove the utility account{' '}
              <span className="font-mono">
                {values.registration.tenant.accounts[values.selectedAccountIdx]?.number || '(no number)'}
              </span>{' '}
              and all of its service agreements from this onboarding record. Changes take effect after you save.
            </p>
          </div>
        </div>
        {hasAnyPma && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 dark:text-amber-100">
              The tenant may need to re-authorize the updated list on a new PMA.
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button color="slate" onClick={() => setShowRemoveUaConfirm(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              const uaIdx = Number(values.selectedAccountIdx)
              const newAccounts = values.registration.tenant.accounts.filter((_, i) => i !== uaIdx)
              const nextIdx = Math.max(0, Math.min(uaIdx, newAccounts.length - 1))
              // Update accounts + selection in a single values update so we never render with a
              // stale selectedAccountIdx pointing past the end of the new (shorter) accounts array.
              void formik.setValues({
                ...values,
                registration: {
                  ...values.registration,
                  tenant: { ...values.registration.tenant, accounts: newAccounts },
                },
                selectedAccountIdx: `${nextIdx}` as unknown as number,
                selectedAgreementIdx: '0' as unknown as number,
              })
              setShowRemoveUaConfirm(false)
            }}
          >
            Remove UA
          </Button>
        </div>
      </ConfirmDialogShell>
      <ConfirmDialogShell isOpen={showRemoveSaConfirm}>
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 shrink-0">
            <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Remove Service Agreement SA {Number(values.selectedAccountIdx) + 1}-{Number(values.selectedAgreementIdx) + 1}?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This will remove the service agreement{' '}
              <span className="font-mono">
                {values.registration.tenant.accounts[values.selectedAccountIdx]?.agreements[values.selectedAgreementIdx]?.number ||
                  '(no number)'}
              </span>{' '}
              from this utility account. Changes take effect after you save.
            </p>
          </div>
        </div>
        {hasAnyPma && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 dark:text-amber-100">
              The tenant may need to re-authorize the updated list on a new PMA.
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <Button color="slate" onClick={() => setShowRemoveSaConfirm(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              const uaIdx = Number(values.selectedAccountIdx)
              const saIdx = Number(values.selectedAgreementIdx)
              const currentAgreements = values.registration.tenant.accounts[uaIdx]?.agreements ?? []
              const newAgreements = currentAgreements.filter((_, i) => i !== saIdx)
              const nextIdx = Math.max(0, Math.min(saIdx, newAgreements.length - 1))
              // Atomic update so selectedAgreementIdx is never briefly stale past the end of the new agreements array.
              const newAccounts = values.registration.tenant.accounts.map((acct, i) =>
                i === uaIdx ? { ...acct, agreements: newAgreements } : acct,
              )
              void formik.setValues({
                ...values,
                registration: {
                  ...values.registration,
                  tenant: { ...values.registration.tenant, accounts: newAccounts },
                },
                selectedAgreementIdx: `${nextIdx}` as unknown as number,
              })
              setShowRemoveSaConfirm(false)
            }}
          >
            Remove SA
          </Button>
        </div>
      </ConfirmDialogShell>
      <ArchiveOnboardingDialog
        isOpen={showArchiveConfirm}
        businessName={props.onboarding.tenant?.name ?? 'this record'}
        progressBeyondInvite={progressBeyondInvite}
        onCancel={() => setShowArchiveConfirm(false)}
        onConfirm={handleArchiveConfirm}
      />
      <ConfirmDialogShell isOpen={showUnarchiveConfirm}>
        <div className="space-y-4">
          <div role="heading" aria-level={3} className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Restore onboarding record?
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            This will return the record to the active onboarding list and re-enable editing.
          </p>
          <div className="flex justify-end gap-2">
            <Button color="slate" onClick={() => setShowUnarchiveConfirm(false)} disabled={isUnarchiving}>
              Cancel
            </Button>
            <Button color="indigo" onClick={handleUnarchiveConfirm} disabled={isUnarchiving} isSaving={isUnarchiving}>
              Unarchive
            </Button>
          </div>
        </div>
      </ConfirmDialogShell>
    </div>
  )
}
